import { useState, useEffect, useCallback, useRef } from "react";
import api from "@/lib/api";
import { PageHeader } from "@/components/common/PageHeader";
import { KpiCard } from "@/components/common/KpiCard";
import { SearchInput, Pager } from "@/components/common/Pager";
import { Loader } from "@/components/common/Loader";
import { PackageSearch, Boxes, Eye, Tag, Image as ImageIcon, Save, X, Newspaper, Plus, Trash2, Layers, Users, Upload, Loader2, GripVertical, ChevronUp, ChevronDown } from "lucide-react";
import { toast } from "sonner";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { BuyerScanReview } from "@/components/WebshopPanel";

const API = process.env.REACT_APP_BACKEND_URL;
const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function ProductImg({ id, hasImage, className }) {
  if (!hasImage) {
    return (
      <div className={`flex items-center justify-center bg-[#f0f1f3] text-[#c1c6cd] ${className}`}>
        <ImageIcon size={22} />
      </div>
    );
  }
  return <img src={`${API}/api/shop/media/${id}`} alt="" loading="lazy" className={`${className} object-contain bg-white`} />;
}

export default function Pim() {
  const [stats, setStats] = useState(null);
  const [cats, setCats] = useState([]);
  const [cat, setCat] = useState("");
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);
  const [data, setData] = useState({ items: [], total: 0, pages: 1 });
  const [loading, setLoading] = useState(true);
  const [edit, setEdit] = useState(null);
  const [newsOpen, setNewsOpen] = useState(false);
  const [catsOpen, setCatsOpen] = useState(false);
  const [scanOpen, setScanOpen] = useState(false);
  const [groupsOpen, setGroupsOpen] = useState(false);
  const [groups, setGroups] = useState([]);

  useEffect(() => {
    api.get("/pim/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/pim/categories").then((r) => setCats(r.data.categories || [])).catch(() => {});
    api.get("/pim/customer-groups").then((r) => setGroups(r.data.groups || [])).catch(() => {});
  }, []);
  useEffect(() => { setPage(1); }, [q, cat]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/pim/products?page=${page}&page_size=24&q=${encodeURIComponent(q)}&category=${cat}`);
      setData(data);
    } finally { setLoading(false); }
  }, [page, q, cat]);
  useEffect(() => { load(); }, [load]);

  const saveEdit = async () => {
    try {
      await api.put(`/pim/products/${edit.id}`, {
        title: edit.title, price: Number(edit.price), description: edit.description,
        visible: edit.visible, group_prices: edit.groupPrices || {}, capex: !!edit.capex,
      });
      toast.success("Product updated");
      setEdit(null); load();
    } catch (e) { toast.error("Update failed"); }
  };

  const roots = cats.filter((c) => !c.parent_id);

  return (
    <div data-testid="pim-page">
      <PageHeader title="eCommerce PIM" accent="#7c3aed" icon={PackageSearch}
        subtitle="Product Information Management — the M&S GNFR catalogue powering the online storefront.">
        <div className="flex flex-wrap gap-2">
          <button data-testid="pim-categories-btn" onClick={() => setCatsOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-2 text-sm font-medium text-[#32363a] hover:border-[#7c3aed]"><Layers size={15} /> Categories</button>
          <button data-testid="pim-groups-btn" onClick={() => setGroupsOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-2 text-sm font-medium text-[#32363a] hover:border-[#7c3aed]"><Users size={15} /> Customer Groups</button>
          <button data-testid="pim-news-btn" onClick={() => setNewsOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-2 text-sm font-medium text-[#32363a] hover:border-[#7c3aed]"><Newspaper size={15} /> News</button>
          <button data-testid="pim-scan-photos-btn" onClick={() => setScanOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-2 text-sm font-medium text-[#32363a] hover:border-[#7c3aed]"><ImageIcon size={15} /> Buyer Scan Photos</button>
        </div>
      </PageHeader>
      <Dialog open={scanOpen} onOpenChange={setScanOpen}>
        <DialogContent className="max-w-4xl">
          <DialogHeader><DialogTitle>Buyer scan photos</DialogTitle>
            <DialogDescription>Photos buyers confirmed with “This is it”. Remove any that show the wrong product so scans stay accurate.</DialogDescription></DialogHeader>
          {scanOpen && <BuyerScanReview />}
        </DialogContent>
      </Dialog>
      <NewsManager open={newsOpen} onClose={() => setNewsOpen(false)} />
      <CategoryManager open={catsOpen} onClose={() => setCatsOpen(false)} />
      <GroupManager open={groupsOpen} onClose={() => setGroupsOpen(false)} />

      {stats && (
        <div className="mb-6 grid grid-cols-2 gap-3 lg:grid-cols-4">
          <KpiCard testId="pim-kpi-products" label="Total Products" value={stats.products.toLocaleString()} accent="#7c3aed" icon={Boxes} />
          <KpiCard testId="pim-kpi-visible" label="Live on Storefront" value={stats.visible.toLocaleString()} accent="#10B981" icon={Eye} />
          <KpiCard testId="pim-kpi-priced" label="Priced Items" value={stats.priced.toLocaleString()} accent="#0a6ed1" icon={Tag} />
          <KpiCard testId="pim-kpi-cats" label="Categories" value={stats.categories.toLocaleString()} accent="#F97316" icon={PackageSearch} />
        </div>
      )}

      <div className="mb-4 flex flex-wrap items-center gap-3">
        <SearchInput value={q} onChange={setQ} placeholder="Search products, SAP code…" testId="pim-search" />
        <select data-testid="pim-cat-filter" value={cat} onChange={(e) => setCat(e.target.value)}
          className="rounded-md border border-[#c8cfda] bg-white px-3 py-2 text-sm text-[#32363a] outline-none focus:border-[#7c3aed]">
          <option value="">All categories</option>
          {roots.map((c) => (
            <option key={c.id} value={c.id}>{c.title} ({c.product_count})</option>
          ))}
        </select>
      </div>

      {loading ? <Loader label="Loading catalogue…" /> : (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" data-testid="pim-grid">
            {data.items.map((p) => (
              <div key={p.id} data-testid={`pim-card-${p.id}`}
                className="flex gap-3 rounded-lg border border-[#e5e9f0] bg-white p-3 shadow-sm transition hover:shadow-md">
                <ProductImg id={p.id} hasImage={p.has_image} className="h-20 w-20 shrink-0 rounded-md" />
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-medium text-[#32363a]" title={p.title}>{p.title}</div>
                  <div className="mt-0.5 font-mono text-[11px] text-[#6a6d70]">{p.sap_code}</div>
                  <div className="mt-1 text-sm font-semibold text-[#7c3aed]">{p.price > 0 ? gbp(p.price) : "POA"}</div>
                  <div className="mt-2 flex items-center gap-2">
                    {!p.visible && <span className="rounded bg-[#fde8e8] px-1.5 py-0.5 text-[10px] font-medium text-[#bb0000]">Hidden</span>}
                    {p.visible && <span className="rounded bg-[#e6f4ea] px-1.5 py-0.5 text-[10px] font-medium text-[#137333]">Live</span>}
                    <button data-testid={`pim-edit-${p.id}`} onClick={() => setEdit({ ...p, groupPrices: { ...(p.prices || {}) } })}
                      className="ml-auto text-[11px] font-medium text-[#7c3aed] hover:underline">Edit</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <Pager page={page} pages={data.pages} total={data.total} pageSize={24} onPage={setPage} testId="pim-pager" />
        </>
      )}

      <Dialog open={!!edit} onOpenChange={(o) => !o && setEdit(null)}>
        <DialogContent data-testid="pim-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit product</DialogTitle>
            <DialogDescription className="font-mono text-xs">{edit?.sap_code}</DialogDescription>
          </DialogHeader>
          {edit && (
            <div className="grid gap-3">
              <label className="text-xs font-medium text-[#6a6d70]">Title
                <input data-testid="pim-edit-title" value={edit.title || ""} onChange={(e) => setEdit({ ...edit, title: e.target.value })}
                  className="mt-1 w-full rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
              </label>
              <label className="text-xs font-medium text-[#6a6d70]">Price (£)
                <input data-testid="pim-edit-price" type="number" step="0.01" value={edit.price ?? 0} onChange={(e) => setEdit({ ...edit, price: e.target.value })}
                  className="mt-1 w-full rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
                <span className="mt-0.5 block text-[10px] text-[#9aa0a6]">Base price — used for any brand without its own price below.</span>
              </label>
              {groups.length > 0 && (
                <div className="rounded-md border border-[#e5e9f0] p-3" data-testid="pim-group-prices">
                  <div className="mb-2 text-xs font-semibold text-[#6a6d70]">Per-brand price (£)</div>
                  <div className="grid gap-2">
                    {groups.map((g) => (
                      <label key={g.code} className="flex items-center gap-2 text-xs text-[#32363a]">
                        <span className="inline-block h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: g.accent || "#c1c6cd" }} />
                        <span className="w-28 shrink-0 truncate">{g.label}</span>
                        <input data-testid={`pim-gprice-${g.code}`} type="number" step="0.01"
                          value={edit.groupPrices?.[g.code] ?? ""}
                          onChange={(e) => setEdit({ ...edit, groupPrices: { ...(edit.groupPrices || {}), [g.code]: e.target.value } })}
                          placeholder={edit.base_price ? `Base £${edit.base_price}` : "Base price"}
                          className="flex-1 rounded-md border border-[#c8cfda] px-3 py-1.5 text-sm outline-none focus:border-[#7c3aed]" />
                      </label>
                    ))}
                  </div>
                </div>
              )}
              <label className="text-xs font-medium text-[#6a6d70]">Description
                <textarea data-testid="pim-edit-desc" rows={3} value={edit.description || ""} onChange={(e) => setEdit({ ...edit, description: e.target.value })}
                  className="mt-1 w-full rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
              </label>
              <label className="flex items-center gap-2 text-sm text-[#32363a]">
                <input data-testid="pim-edit-visible" type="checkbox" checked={!!edit.visible} onChange={(e) => setEdit({ ...edit, visible: e.target.checked })} />
                Visible on storefront
              </label>
              <label className="flex items-center gap-2 text-sm text-[#32363a]">
                <input data-testid="pim-edit-capex" type="checkbox" checked={!!edit.capex} onChange={(e) => setEdit({ ...edit, capex: e.target.checked })} />
                Cap Ex item (orders containing it need Cap Ex approval)
              </label>
            </div>
          )}
          <DialogFooter>
            <button onClick={() => setEdit(null)} className="inline-flex items-center gap-1.5 rounded-md border border-[#c8cfda] px-3 py-2 text-sm text-[#32363a] hover:bg-[#f5f6f7]"><X size={15} />Cancel</button>
            <button data-testid="pim-edit-save" onClick={saveEdit} className="inline-flex items-center gap-1.5 rounded-md bg-[#7c3aed] px-3 py-2 text-sm font-semibold text-white hover:bg-[#6d28d9]"><Save size={15} />Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function CategoryManager({ open, onClose }) {
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState({});
  const dragRef = useRef(null); // {id, parent}
  const load = () => api.get("/pim/categories").then((r) => setCats(r.data.categories || []));
  useEffect(() => { if (open) load(); }, [open]);

  const byOrder = (a, b) => (a.ordering || 0) - (b.ordering || 0) || ((a.title || "") > (b.title || "") ? 1 : -1);
  const tops = cats.filter((c) => !c.parent_id).sort(byOrder);
  const subsOf = (id) => cats.filter((c) => c.parent_id === id).sort(byOrder);
  const setField = (id, k, v) => setCats((cur) => cur.map((c) => (c.id === id ? { ...c, [k]: v } : c)));

  const persistOrder = async (ids) => {
    setCats((cur) => cur.map((c) => (ids.includes(c.id) ? { ...c, ordering: ids.indexOf(c.id) } : c)));
    try { await api.post("/pim/categories/reorder", { ordered_ids: ids }); toast.success("Order saved", { duration: 900 }); }
    catch (e) { toast.error("Reorder failed"); load(); }
  };
  const moveInList = (list, id, dir) => {
    const ids = list.map((c) => c.id); const i = ids.indexOf(id); const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]]; persistOrder(ids);
  };
  const onDrop = (list, parent, targetId) => {
    const d = dragRef.current; dragRef.current = null;
    if (!d || d.parent !== parent || d.id === targetId) return;
    const ids = list.map((c) => c.id);
    const from = ids.indexOf(d.id), to = ids.indexOf(targetId);
    if (from < 0 || to < 0) return;
    ids.splice(to, 0, ids.splice(from, 1)[0]); persistOrder(ids);
  };
  const save = async (c) => {
    setBusy(c.id);
    try { await api.put(`/pim/categories/${c.id}`, { title: c.title }); toast.success("Category saved"); }
    catch (e) { toast.error("Save failed"); } finally { setBusy(null); }
  };
  const upload = async (c, file) => {
    if (!file) return; setBusy(c.id);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post(`/pim/categories/${c.id}/image`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Tile image updated"); setField(c.id, "image_path", `pim/cat-${c.id}`);
    } catch (e) { toast.error("Upload failed"); } finally { setBusy(null); }
  };

  // render helper (NOT a component — avoids input remount/focus loss on re-render)
  const renderRow = (c, list, parent, sub, canDrag) => (
    <div data-testid={`cat-row-${c.id}`} draggable={canDrag}
      onDragStart={canDrag ? (() => { dragRef.current = { id: c.id, parent }; }) : undefined}
      onDragOver={canDrag ? ((e) => e.preventDefault()) : undefined}
      onDrop={canDrag ? (() => onDrop(list, parent, c.id)) : undefined}
      className={`flex items-center gap-2 rounded-md border border-[#e5e9f0] p-2 ${sub ? "bg-[#fafbfc]" : "bg-white"}`}>
      <span className={`shrink-0 ${canDrag ? "cursor-grab text-[#c1c6cd]" : "text-[#e5e9f0]"}`} title={canDrag ? "Drag to reorder" : "Clear search to reorder"}><GripVertical size={16} /></span>
      <div className="flex shrink-0 flex-col">
        <button data-testid={`cat-up-${c.id}`} onClick={() => moveInList(list, c.id, -1)} disabled={!canDrag} className="text-[#9aa0a6] hover:text-[#7c3aed] disabled:opacity-30"><ChevronUp size={12} /></button>
        <button data-testid={`cat-down-${c.id}`} onClick={() => moveInList(list, c.id, 1)} disabled={!canDrag} className="text-[#9aa0a6] hover:text-[#7c3aed] disabled:opacity-30"><ChevronDown size={12} /></button>
      </div>
      {c.image_path
        ? <img src={`${API}/api/shop/category-media/${c.id}?w=120&t=${Date.now()}`} alt="" className="h-9 w-9 shrink-0 rounded object-cover" />
        : <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded bg-[#f0f1f3] text-[#c1c6cd]"><ImageIcon size={14} /></div>}
      <input data-testid={`cat-title-${c.id}`} value={c.title || ""} onChange={(e) => setField(c.id, "title", e.target.value)}
        className="min-w-0 flex-1 rounded-md border border-[#c8cfda] px-2 py-1.5 text-sm outline-none focus:border-[#7c3aed]" />
      {!sub && subsOf(c.id).length > 0 &&
        <button data-testid={`cat-expand-${c.id}`} onClick={() => setExpanded((e) => ({ ...e, [c.id]: !e[c.id] }))}
          className="shrink-0 rounded border border-[#c8cfda] px-2 py-1 text-[10px] font-medium text-[#6a6d70] hover:border-[#7c3aed]">{expanded[c.id] ? "Hide" : `${subsOf(c.id).length} subs`}</button>}
      <label className="shrink-0 cursor-pointer rounded-md border border-[#c8cfda] p-2 text-[#32363a] hover:border-[#7c3aed]" title="Upload tile image">
        <Upload size={14} /><input type="file" accept="image/*" className="hidden" onChange={(e) => upload(c, e.target.files?.[0])} /></label>
      <button data-testid={`cat-save-${c.id}`} onClick={() => save(c)} disabled={busy === c.id}
        className="shrink-0 rounded-md bg-[#7c3aed] p-2 text-white hover:bg-[#6d28d9] disabled:opacity-50">{busy === c.id ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}</button>
    </div>
  );

  const visibleTops = q ? tops.filter((c) => (c.title || "").toLowerCase().includes(q.toLowerCase())) : tops;
  const canDragTops = !q;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pim-categories-dialog" className="max-w-2xl">
        <DialogHeader><DialogTitle>Manage Categories</DialogTitle>
          <DialogDescription>Drag the handle (or use the ▲▼ arrows) to reorder top-level and sub-categories — the storefront follows this order. Expand a category to reorder its sub-categories. Rename and upload tile images here too.</DialogDescription></DialogHeader>
        <input data-testid="cat-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search top categories…"
          className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
        <div className="max-h-[60vh] space-y-2 overflow-y-auto pr-1">
          {visibleTops.map((c) => (
            <div key={c.id}>
              {renderRow(c, tops, null, false, canDragTops)}
              {expanded[c.id] && (
                <div className="ml-8 mt-1 space-y-1">
                  {subsOf(c.id).map((s) => renderRow(s, subsOf(c.id), c.id, true, true))}
                  {subsOf(c.id).length === 0 && <div className="px-2 py-1 text-xs text-[#9aa0a6]">No sub-categories</div>}
                </div>
              )}
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function GroupManager({ open, onClose }) {
  const [groups, setGroups] = useState([]);
  const [form, setForm] = useState({ code: "", label: "" });
  const load = () => api.get("/pim/customer-groups").then((r) => setGroups(r.data.groups || []));
  useEffect(() => { if (open) load(); }, [open]);
  const setField = (code, k, v) => setGroups((cur) => cur.map((g) => (g.code === code ? { ...g, [k]: v } : g)));
  const add = async () => {
    if (!form.code || !form.label) { toast.error("Enter a sold-to code and name"); return; }
    try { await api.post("/pim/customer-groups", form); setForm({ code: "", label: "" }); toast.success("Customer group added"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const save = async (g) => {
    try { await api.put(`/pim/customer-groups/${g.code}`, { label: g.label, accent: g.accent, receipt_email: g.receipt_email }); toast.success(`${g.label} saved`); }
    catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const del = async (code) => {
    try { await api.delete(`/pim/customer-groups/${code}`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Cannot delete"); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pim-groups-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Brands &amp; Customer Groups</DialogTitle>
          <DialogDescription>Set each brand's accent colour and live order-confirmation email. Assign products to brands per material in MM.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-[1fr_1.5fr_auto] gap-2 rounded-lg border border-[#e5e9f0] p-3">
          <input data-testid="group-code" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} placeholder="Sold-to code"
            className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
          <input data-testid="group-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} placeholder="Brand name"
            className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
          <button data-testid="group-add" onClick={add} className="inline-flex items-center gap-1.5 rounded-md bg-[#7c3aed] px-3 py-2 text-sm font-semibold text-white hover:bg-[#6d28d9]"><Plus size={15} /></button>
        </div>
        <div className="max-h-[55vh] space-y-2 overflow-y-auto">
          {groups.map((g) => (
            <div key={g.code} data-testid={`group-row-${g.code}`} className="rounded-md border border-[#e5e9f0] p-2.5">
              <div className="flex items-center gap-2">
                <input type="color" data-testid={`group-accent-${g.code}`} value={g.accent || "#D0313A"} onChange={(e) => setField(g.code, "accent", e.target.value)}
                  className="h-8 w-8 shrink-0 cursor-pointer rounded border border-[#c8cfda] bg-white" title="Brand accent colour" />
                <input data-testid={`group-label-edit-${g.code}`} value={g.label || ""} onChange={(e) => setField(g.code, "label", e.target.value)}
                  className="min-w-0 flex-1 rounded-md border border-[#c8cfda] px-2 py-1.5 text-sm outline-none focus:border-[#7c3aed]" />
                <span className="shrink-0 font-mono text-[10px] text-[#9aa0a6]">{g.code}</span>
                <button data-testid={`group-save-${g.code}`} onClick={() => save(g)} className="shrink-0 rounded-md bg-[#7c3aed] p-2 text-white hover:bg-[#6d28d9]"><Save size={14} /></button>
                {g.code !== "28732" && <button data-testid={`group-del-${g.code}`} onClick={() => del(g.code)} className="shrink-0 rounded p-1 text-[#bb0000] hover:bg-[#fde8e8]"><Trash2 size={14} /></button>}
              </div>
              <input data-testid={`group-email-${g.code}`} type="email" value={g.receipt_email || ""} onChange={(e) => setField(g.code, "receipt_email", e.target.value)}
                placeholder="Order confirmation email for live receipts (optional)"
                className="mt-2 w-full rounded-md border border-[#c8cfda] px-2 py-1.5 text-xs outline-none focus:border-[#7c3aed]" />
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function NewsManager({ open, onClose }) {
  const [news, setNews] = useState([]);
  const [form, setForm] = useState({ title: "", body: "" });
  const load = () => api.get("/pim/news").then((r) => setNews(r.data.news || []));
  useEffect(() => { if (open) load(); }, [open]);
  const add = async () => {
    if (!form.title || !form.body) { toast.error("Enter a title and body"); return; }
    await api.post("/pim/news", form); setForm({ title: "", body: "" }); toast.success("News item added"); load();
  };
  const del = async (id) => { await api.delete(`/pim/news/${id}`); load(); };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="pim-news-dialog" className="max-w-lg">
        <DialogHeader><DialogTitle>Webshop News Feed</DialogTitle>
          <DialogDescription>News items shown on every buyer's homepage.</DialogDescription></DialogHeader>
        <div className="grid gap-2 rounded-lg border border-[#e5e9f0] p-3">
          <input data-testid="news-title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Headline"
            className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
          <textarea data-testid="news-body" rows={2} value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Details…"
            className="rounded-md border border-[#c8cfda] px-3 py-2 text-sm outline-none focus:border-[#7c3aed]" />
          <button data-testid="news-add" onClick={add} className="inline-flex items-center justify-center gap-1.5 rounded-md bg-[#7c3aed] px-3 py-2 text-sm font-semibold text-white hover:bg-[#6d28d9]"><Plus size={15} /> Add news item</button>
        </div>
        <div className="max-h-64 space-y-2 overflow-y-auto">
          {news.map((n) => (
            <div key={n.id} className="flex items-start justify-between gap-2 rounded-md border border-[#e5e9f0] p-2.5">
              <div><div className="text-sm font-medium text-[#32363a]">{n.title}</div><div className="text-xs text-[#6a6d70]">{n.body}</div></div>
              <button data-testid={`news-del-${n.id}`} onClick={() => del(n.id)} className="rounded p-1 text-[#bb0000] hover:bg-[#fde8e8]"><Trash2 size={14} /></button>
            </div>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
}
